import type {
  CatalogBackedJobResult,
  ContentCentralCatalogDefinition,
  ContentCentralCatalogPage,
  ContentCentralConnectionRequest,
  DestinationConnectionSummary,
  JobDefinition,
} from '../shared/desktop-api';
import { askContentCentralKey } from './ContentCentralKeyDialog';
import { responseWords } from './problem-words';
import type { PaigeCcCatalog, PaigeCcCatalogs } from './product-destination-types';

/** Where the start calls go: the API's root and the company. */
export interface StartOptions {
  apiOrigin: string;
  tenant?: string | undefined;
}

/** How a connection is named to the API: a job's destination, or one typed here (sealed by Paige). */
type ConnectionRef = { jobId: string; destinationId: string } | { sealed: string };

interface Listing {
  connections: { jobId: string; destinationId: string; name: string; siteUrl: string }[];
  uses: {
    connectionDestinationId: string;
    catalog: string;
    jobId: string;
    workspaceJobId: string | null;
    jobName: string;
  }[];
}

interface Made {
  id: string;
  workspaceJobId: string | null;
  job: JobDefinition | null;
  warnings: string[];
}

/** What the start remembers between calls: the connections by id with their sites, catalog names, and catalogs in use. */
interface Memory {
  send: <T>(path: string, method: 'GET' | 'POST', body?: unknown) => Promise<T>;
  known: Map<string, { ref: ConnectionRef; siteUrl: string }>;
  names: Map<string, string>;
  uses: Listing['uses'];
  bind: (workspaceJobId: string, paigeJobId: string) => void;
}

/**
 * New job › From Content Central in Paige (parity S-22): CapturePoint's start, with Paige's destination
 * connection in place of the desktop's saved one. The connections are the company's Content Central destinations;
 * "Add connection" asks for the key in Paige's own key window and hands it to the API, which checks it and
 * returns it sealed (the page holds only that). Every catalog read and Create job goes through the API with the
 * connection's key; the job comes back with Paige's ids, its channel bound as a job Paige created.
 */
export function contentCentralStart(
  apiOrigin: string,
  tenant: string | undefined,
  bind: Memory['bind'],
) {
  const options: StartOptions = { apiOrigin, tenant };
  const memory: Memory = {
    send: (path, method, body) => request(options, path, method, body),
    known: new Map(),
    names: new Map(),
    uses: [],
    bind,
  };
  return {
    listDestinationConnections: () => listConnections(memory),
    connectContentCentral: (asked: ContentCentralConnectionRequest) => connect(memory, asked),
    listContentCentralCatalogs: (connectionId: string, query?: string, cursor?: string) =>
      catalogs(memory, connectionId, { query, cursor }),
    getContentCentralCatalog: async (connectionId: string, catalogId: string) =>
      definitionOf(
        await memory.send<PaigeCcCatalog>('/content-central/catalogs/read', 'POST', {
          connection: refOf(memory, connectionId),
          catalogId,
        }),
      ),
    findContentCentralJob: async (connectionId: string, catalogId: string) =>
      findJob(memory, connectionId, catalogId),
    createContentCentralJob: (connectionId: string, catalogId: string, jobName: string) =>
      createJob(memory, connectionId, { catalogId, jobName }),
  };
}

async function listConnections(memory: Memory): Promise<DestinationConnectionSummary[]> {
  const listing = await memory.send<Listing>('/content-central/connections', 'GET');
  memory.uses = listing.uses;
  return listing.connections.map((c) => {
    const connectionId = `d:${c.jobId}:${c.destinationId}`;
    memory.known.set(connectionId, {
      ref: { jobId: c.jobId, destinationId: c.destinationId },
      siteUrl: c.siteUrl,
    });
    return summaryOf(connectionId, c.name, c.siteUrl);
  });
}

async function connect(memory: Memory, asked: ContentCentralConnectionRequest) {
  const apiKey = await askContentCentralKey(asked.displayName);
  if (!apiKey) return null;
  const made = await memory.send<{ connection: string; name: string; siteUrl: string }>(
    '/content-central/connections',
    'POST',
    {
      name: asked.displayName,
      siteUrl: asked.baseUrl,
      apiKey,
    },
  );
  const connectionId = `s:${crypto.randomUUID()}`;
  memory.known.set(connectionId, { ref: { sealed: made.connection }, siteUrl: made.siteUrl });
  return summaryOf(connectionId, made.name, made.siteUrl);
}

async function catalogs(
  memory: Memory,
  connectionId: string,
  page: { query?: string | undefined; cursor?: string | undefined },
): Promise<ContentCentralCatalogPage> {
  const found = await memory.send<PaigeCcCatalogs>('/content-central/catalogs/search', 'POST', {
    connection: refOf(memory, connectionId),
    ...page,
  });
  for (const item of found.items) memory.names.set(item.id, item.name);
  return {
    items: found.items.map((c) => ({ ...c, description: null })),
    nextCursor: found.nextCursor,
  };
}

/** A job of the company that already files into this catalog on this site, for "open it or make a separate job". */
function findJob(memory: Memory, connectionId: string, catalogId: string) {
  const site = memory.known.get(connectionId)?.siteUrl;
  const siteOf = (destinationId: string) =>
    [...memory.known.values()].find(
      (k) => 'destinationId' in k.ref && k.ref.destinationId === destinationId,
    )?.siteUrl;
  const named = memory.names.get(catalogId);
  const use = memory.uses.find(
    (u) =>
      siteOf(u.connectionDestinationId) === site &&
      (u.catalog === catalogId || u.catalog === named),
  );
  return use ? { jobId: use.workspaceJobId ?? use.jobId, jobName: use.jobName } : null;
}

async function createJob(
  memory: Memory,
  connectionId: string,
  asked: { catalogId: string; jobName: string },
): Promise<CatalogBackedJobResult> {
  const made = await memory.send<Made>('/content-central/jobs', 'POST', {
    connection: refOf(memory, connectionId),
    catalogId: asked.catalogId,
    name: asked.jobName,
  });
  if (!made.job || !made.workspaceJobId)
    throw new Error(
      'The job was made, but Cortexa has not opened it yet. Open it from your jobs in a minute.',
    );
  memory.bind(made.workspaceJobId, made.id);
  window.paigeWorkbench?.created?.(made.id);
  return {
    job: made.job,
    binding: bindingOf(made.workspaceJobId, asked.catalogId, memory.names.get(asked.catalogId)),
    warnings: made.warnings,
  };
}

function refOf(memory: Memory, connectionId: string): ConnectionRef {
  const found = memory.known.get(connectionId);
  if (!found) throw new Error('Choose the connection again.');
  return found.ref;
}

function summaryOf(
  connectionId: string,
  name: string,
  siteUrl: string,
): DestinationConnectionSummary {
  const now = new Date().toISOString();
  return {
    connectionId,
    kind: 'content-central',
    displayName: name,
    baseUrl: siteUrl,
    serverInstanceId: '',
    protocolVersion: '',
    redactedKeyPrefix: '',
    capabilities: [],
    createdAt: now,
    updatedAt: now,
  };
}

/** Paige's catalog in the desktop's shape: a field Paige may fill is the connection's own ("capturepoint"). */
export function definitionOf(catalog: PaigeCcCatalog): ContentCentralCatalogDefinition {
  const noFiling = {
    folderBuildingEnabled: false,
    fileBuildingEnabled: false,
    emptyFieldText: '',
    invalidCharacterReplacement: '_',
    folderItems: [],
    fileItems: [],
  };
  const types = catalog.documentTypes.map((type) => ({
    id: type.id,
    name: type.name,
    canAddDocuments: type.canAddDocuments,
    fields: type.fields.map((field) => ({
      id: field.id,
      name: field.name,
      valueType: field.valueType,
      required: field.required,
      multiple: false,
      valueOwner: field.sendable ? 'capturepoint' : 'content-central',
      choices: [],
      lineItemGroupId: field.lineItem ? 'line-items' : null,
      hidden: false,
      extractable: field.sendable,
      warnings: [],
    })),
    filing: noFiling,
    warnings: [],
  }));
  return {
    catalog: {
      id: catalog.id,
      name: catalog.name,
      documentTypeCount: types.length,
      canAddDocuments: catalog.canAddDocuments,
    },
    documentTypes: types,
  };
}

function bindingOf(
  jobId: string,
  catalogId: string,
  catalogName: string | undefined,
): CatalogBackedJobResult['binding'] {
  const now = new Date().toISOString();
  return {
    bindingId: '',
    jobId,
    destinationKind: 'content-central',
    remoteCatalogId: catalogId,
    remoteCatalogName: catalogName ?? null,
    schemaAuthority: 'destination',
    filingAuthority: 'destination',
    bindingRevision: 0,
    status: 'connected',
    documentTypes: [],
    createdAt: now,
    updatedAt: now,
  };
}

async function request<T>(
  options: StartOptions,
  path: string,
  method: 'GET' | 'POST',
  body?: unknown,
): Promise<T> {
  const response = await fetch(`${options.apiOrigin}${path}`, {
    method,
    credentials: 'include',
    headers: {
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(method === 'POST' ? { 'Idempotency-Key': crypto.randomUUID() } : {}),
      ...(options.tenant ? { 'Paige-Tenant': options.tenant } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) throw new Error(await responseWords(response));
  return (await response.json()) as T;
}
